import { createContext, useContext, useReducer } from 'react';
import { apiService } from '../services/api';
import type { Account } from '../types/account';
import { useAccount } from './AccountContext';
import { useSnackbar } from './SnackbarContext';

const EMPTY_ARRAY: never[] = [];

// 1. Define Filter State and Actions
export interface FilterState {
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  teams: string[];
  sectors: string[];
  clients: string[];
  sources: string[];
  states: string[];
}

type FilterAction =
  | { type: 'SET_DATE_RANGE'; payload: { startDate: Date | null; endDate: Date | null } }
  | { type: 'SET_TEAMS'; payload: string[] }
  | { type: 'SET_SECTORS'; payload: string[] }
  | { type: 'SET_CLIENTS'; payload: string[] }
  | { type: 'SET_SOURCES'; payload: string[] }
  | { type: 'SET_STATES'; payload: string[] };

// Helper type since Account isn't strictly typed with preferences
type AccountWithPreferences = Account & {
  preferences?: {
    insightsFilters?: Partial<FilterState> & {
      dateRange?: { startDate?: string | Date | null; endDate?: string | Date | null };
    };
    [key: string]: unknown;
  };
};

const getDefaultInitialState = (account?: AccountWithPreferences | null): FilterState => {
  const saved = account?.preferences?.insightsFilters;
  if (saved) {
    try {
      // In case date strings need parsing
      return {
        dateRange: {
          startDate: saved.dateRange?.startDate ? new Date(saved.dateRange.startDate) : null,
          endDate: saved.dateRange?.endDate ? new Date(saved.dateRange.endDate) : null,
        },
        teams: saved.teams || EMPTY_ARRAY,
        sectors: saved.sectors || EMPTY_ARRAY,
        clients: saved.clients || EMPTY_ARRAY,
        sources: saved.sources || EMPTY_ARRAY,
        states: saved.states || EMPTY_ARRAY,
      };
    } catch (_e) {
      // Ignore parse error
    }
  }

  // Default to trailing 12 months if no saved defaults
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(endDate.getFullYear() - 1);

  return {
    dateRange: {
      startDate,
      endDate,
    },
    teams: [],
    sectors: [],
    clients: [],
    sources: [],
    states: [],
  };
};

// 2. Create Reducer
const filterReducer = (state: FilterState, action: FilterAction): FilterState => {
  switch (action.type) {
    case 'SET_DATE_RANGE':
      return { ...state, dateRange: action.payload };
    case 'SET_TEAMS':
      return { ...state, teams: action.payload };
    case 'SET_SECTORS':
      return { ...state, sectors: action.payload };
    case 'SET_CLIENTS':
      return { ...state, clients: action.payload };
    case 'SET_SOURCES':
      return { ...state, sources: action.payload };
    case 'SET_STATES':
      return { ...state, states: action.payload };
    default:
      return state;
  }
};

// 3. Create Context and Provider
interface InsightsDashboardContextProps {
  state: FilterState;
  dispatch: React.Dispatch<FilterAction>;
  saveDefaults: () => Promise<void>;
}

const InsightsDashboardContext = createContext<InsightsDashboardContextProps | undefined>(
  undefined
);

export const InsightsDashboardProvider = ({ children }: { children: React.ReactNode }) => {
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();

  // Use a lazy initializer so it only runs once and parses the account preferences
  const [state, dispatch] = useReducer(filterReducer, null, () =>
    getDefaultInitialState(account as AccountWithPreferences)
  );

  const saveDefaults = async () => {
    try {
      await apiService.updateAccountDetails({
        preferences: {
          ...(account as AccountWithPreferences)?.preferences,
          insightsFilters: state,
        },
      });
      showSnackbar('Default filters saved successfully.', 'success');
      await refreshUserDetails();
    } catch (_e) {
      showSnackbar('Failed to save default filters.', 'error');
    }
  };

  return (
    <InsightsDashboardContext.Provider value={{ state, dispatch, saveDefaults }}>
      {children}
    </InsightsDashboardContext.Provider>
  );
};

// 4. Create Custom Hook for easy access
export const useInsightsDashboard = () => {
  const context = useContext(InsightsDashboardContext);
  if (context === undefined) {
    throw new Error('useInsightsDashboard must be used within an InsightsDashboardProvider');
  }
  return context;
};
